import { ReloadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  message,
} from 'antd';
import { useState } from 'react';
import { AiChatConfig, aiChatConfigApi } from '../../../api/aiAssistant';
import AiChatPromptPreview from './AiChatPromptPreview';
import AiChatSkillSyncButton from './AiChatSkillSyncButton';
import AiChatSkillsField from './AiChatSkillsField';
import { validateSkills } from './aiChatSkills';

const THINKING_LEVEL_OPTIONS = [
  { value: 'off', label: '关闭（默认）' },
  { value: 'low', label: '低' },
  { value: 'medium', label: '中' },
  { value: 'high', label: '高' },
];

export default function AiChatConfigCard() {
  const [form] = Form.useForm<AiChatConfig>();
  // 保存成功后自增，驱动 prompt 预览重新拉取（技能改动会改变实际下发的 prompt）
  const [previewToken, setPreviewToken] = useState(0);

  // 是否有未保存的修改：onValuesChange 只在用户交互时触发（setFieldsValue 回填不算），
  // 同步按钮用它拦截「改了没保存就同步」；加载/保存成功回填后归零
  const [dirty, setDirty] = useState(false);

  const { loading: getLoading, refresh } = useRequest(() => aiChatConfigApi.get(), {
    onSuccess: (data) => {
      form.setFieldsValue({ thinkingLevel: 'off', ...data });
      setDirty(false);
    },
  });

  const { loading: updateLoading, run: update } = useRequest(
    (dto: AiChatConfig) => aiChatConfigApi.update(dto),
    {
      manual: true,
      onSuccess: () => {
        message.success('更新成功');
        refresh();
        setPreviewToken((token) => token + 1);
      },
      onError: (e) => {
        message.error(e.message || '更新失败');
      },
    },
  );

  const handleSave = async () => {
    await form.validateFields();
    // validateFields 只返回已注册字段；后端是整对象覆盖，必须取完整 form store，
    // 否则 extraBody / 超时 / 历史截断等未在表单展示的高级字段会被保存动作抹掉
    update(form.getFieldsValue(true));
  };

  return (
    <Card title="AI 助手">
      <Spin spinning={getLoading || updateLoading}>
        <Form form={form} layout="vertical" name="aiChatConfig" onValuesChange={() => setDirty(true)}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="visibleToSuperAdmin"
                label="AI 助手总开关"
                tooltip="关闭（默认）= 除 superadmin 外所有人不可用；开启后拥有「AI 助手」权限码的角色可用。各角色的使用权限、可用工具、数据范围在 系统管理 → 系统角色 中配置；superadmin 恒可用，不受本开关限制"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="enabled"
                label="启用 LLM 调用"
                tooltip="关闭时对话接口直接拒绝（不影响菜单显隐）"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="thinkingLevel"
                label="深度思考级别"
                tooltip="off 时下发 enable_thinking=false（Qwen 系）；低/中/高时下发 enable_thinking=true 与 reasoning_effort（DeepSeek v4 / OpenAI 风格），特殊供应商可用 extra_body 覆盖"
              >
                <Select options={THINKING_LEVEL_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="base_url"
                label="Base URL"
                tooltip="OpenAI 兼容端点，如 https://dashscope.aliyuncs.com/compatible-mode/v1 或 https://api.deepseek.com/v1"
                rules={[{ pattern: /^https?:\/\//, message: '必须为 http(s) 地址' }]}
              >
                <Input placeholder="https://api.deepseek.com/v1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="key" label="API Key">
                <Input.Password placeholder="sk-..." autoComplete="new-password" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="model"
                label="模型"
                tooltip="必须支持 function calling，如 deepseek-chat / qwen-max"
              >
                <Input placeholder="deepseek-chat" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="temperature"
                label="temperature"
                rules={[{ type: 'number', min: 0, max: 2, message: '0-2 之间' }]}
              >
                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} placeholder="留空用模型默认" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxTokens"
                label="maxTokens"
                rules={[{ type: 'integer', min: 1, message: '正整数' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="留空用模型默认" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="systemPrompt"
            label="System Prompt"
            tooltip="留空使用内置默认提示词（含工具使用与图表规则）"
          >
            <Input.TextArea rows={4} placeholder="留空使用内置默认提示词" />
          </Form.Item>
          <Form.Item
            name="skills"
            label="技能"
            tooltip="每个技能是一段场景化提示词，开启后按顺序拼进 System Prompt；技能的示例提问会展示在 AI 助手空会话的引导区"
            rules={[
              {
                validator: async (_rule, value) => {
                  const error = validateSkills(value);
                  if (error) {
                    throw new Error(error);
                  }
                },
              },
            ]}
          >
            <AiChatSkillsField />
          </Form.Item>
          <Form.Item label="Prompt 预览">
            <AiChatPromptPreview refreshToken={previewToken} />
          </Form.Item>
          <Space>
            <Button type="primary" onClick={handleSave} loading={updateLoading}>
              保存
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              刷新
            </Button>
            <AiChatSkillSyncButton dirty={dirty} />
          </Space>
        </Form>
      </Spin>
    </Card>
  );
}
